import { Database, Json } from "../../supabase.types";
import { PostgrestError, SupabaseClient } from "@supabase/supabase-js";
import type { GeoPoint } from "../../models/geo";
import { averagePoint } from "../public/postal-codes";
import dayjs from "@punchup/utils/dayjs";
import { logException } from "@punchup/sentry";

/**
 * Where a comedian's audience came from, for the All Contacts stat row.
 *
 * The two populations are disjoint: an imported row that already subscribes
 * carries `is_on_guest_list = true` and is excluded from `importedContacts`,
 * so the audience size is their sum.
 */
export interface AllContactsSourceCounts {
  /** People who subscribed through the comedian's Punchup page. */
  punchupSubscribers: number;
  /** People brought in by CSV upload who do not already subscribe. */
  importedContacts: number;
  /** The part of `punchupSubscribers` who followed since the start of the day 28 days ago. */
  newSubscribersLast28Days: number;
}

/** Which channel can reach a comedian's audience, for the All Contacts stats. */
export interface AllContactsReachCounts {
  /** People with an email address. */
  withEmail: number;
  /** People with a phone number. */
  withPhone: number;
}

/** A `head: true` read: the row count comes back, the rows do not. */
type HeadCount = PromiseLike<{
  count: number | null;
  error: PostgrestError | null;
}>;

/**
 * Runs head counts in parallel and returns one number per read, in order.
 * Throws on the first database error rather than returning a zero, because a
 * zero here is a figure the page would display as fact.
 */
async function countRows(
  reads: HeadCount[],
  { comedianId, context }: { comedianId: string; context: string }
): Promise<number[]> {
  const results = await Promise.all(reads);

  const failed = results.find((result) => result.error)?.error;
  if (failed) {
    logException(failed, { extra: { comedianId, context } });
    throw new Error(
      `Error counting contacts for comedian ${comedianId} (${context}): ${failed.message}`
    );
  }

  return results.map((result) => result.count ?? 0);
}

// A PostgREST builder is mutable, so every read below starts from a fresh one.

/**
 * Head count over `comedian_dashboard_followers_details`, the per-comedian
 * snapshot that `update_comedian_followers_details()` rebuilds. Subscriber
 * figures therefore lag a new subscription by one rebuild, the same as the
 * audience map. The snapshot carries `email` and `phone`, which the live
 * `user_comedian_follows` table cannot join to through PostgREST.
 */
function countSubscribers(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  return supabase
    .from("comedian_dashboard_followers_details")
    .select("user_id", { count: "exact", head: true })
    .eq("comedian_id", comedianId);
}

/** Head count over imported contacts who do not already subscribe. */
function countImportedContacts(
  supabase: SupabaseClient<Database>,
  comedianId: string
) {
  return supabase
    .from("contacts")
    .select("id", { count: "exact", head: true })
    .eq("comedian_id", comedianId)
    .eq("is_on_guest_list", false);
}

/**
 * Counts the two audience sources and the subscribers gained in the last 28
 * days, the newsletter overview's window. No contact rows cross the wire. The snapshot's `created_at` is the
 * follow's own `created_at`, so the 28-day cut reads it directly.
 */
export async function getAllContactsSourceCounts(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<AllContactsSourceCounts> {
  const since = dayjs().startOf("day").subtract(28, "day").toISOString();
  const [punchupSubscribers, importedContacts, newSubscribersLast28Days] =
    await countRows(
      [
        countSubscribers(supabase, comedianId),
        countImportedContacts(supabase, comedianId),
        countSubscribers(supabase, comedianId).gte("created_at", since),
      ],
      { comedianId, context: "getAllContactsSourceCounts" }
    );

  return { punchupSubscribers, importedContacts, newSubscribersLast28Days };
}

/**
 * Counts how much of a comedian's audience each channel reaches. The two
 * populations are disjoint on `is_on_guest_list`, so the sums do not
 * double-count.
 */
export async function getAllContactsReachCounts(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<AllContactsReachCounts> {
  const [subscriberEmail, subscriberPhone, importedEmail, importedPhone] =
    await countRows(
      [
        countSubscribers(supabase, comedianId).not("email", "is", null),
        countSubscribers(supabase, comedianId).not("phone", "is", null),
        countImportedContacts(supabase, comedianId).not("email", "is", null),
        countImportedContacts(supabase, comedianId).not("phone", "is", null),
      ],
      { comedianId, context: "getAllContactsReachCounts" }
    );

  return {
    withEmail: subscriberEmail + importedEmail,
    withPhone: subscriberPhone + importedPhone,
  };
}

/** One point on the audience map: a city and how many contacts live there. */
export type AudienceMapCity =
  Database["public"]["Functions"]["get_comedian_followers_by_city"]["Returns"][number];

/**
 * Reads contact counts per city for the audience map.
 * The database groups the `audience_contacts` view, both sources, through
 * `get_comedian_followers_by_city`, so a few hundred rows come back whatever
 * the audience size. A contact counts only where its zipcode gives it a
 * point. Subscribers carry the snapshot's lag, as in `countSubscribers`.
 */
export async function getAudienceMapCityCounts(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<AudienceMapCity[]> {
  const { data, error } = await supabase.rpc("get_comedian_followers_by_city", {
    p_comedian_id: comedianId,
  });

  if (error) {
    logException(error, {
      extra: { comedianId, context: "getAudienceMapCityCounts" },
    });
    throw new Error(
      `Error fetching audience map cities for comedian ${comedianId}: ${error.message}`
    );
  }

  return data ?? [];
}

/** One city of the audience, by name only, for the list's city dropdown. */
export type AudienceCityName =
  Database["public"]["Functions"]["get_audience_cities"]["Returns"][number];

/**
 * Reads the names of the cities a comedian's audience lives in, both sources,
 * by city then state, through `get_audience_cities`. The list's city dropdown
 * needs no counts, so it skips the group-by `getAudienceMapCityCounts` runs.
 */
export async function getAudienceCities(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<AudienceCityName[]> {
  const { data, error } = await supabase.rpc("get_audience_cities", {
    p_comedian_id: comedianId,
  });

  if (error) {
    logException(error, {
      extra: { comedianId, context: "getAudienceCities" },
    });
    throw new Error(
      `Error fetching audience cities for comedian ${comedianId}: ${error.message}`
    );
  }

  return data ?? [];
}

/** One upcoming show and the point it plays at, for the audience map's city list. */
export interface AudienceUpcomingShow {
  /** When the show starts, as the schema stores it. */
  datetime: string;
  venueId: string;
  venue: string;
  latitude: number;
  longitude: number;
}

/** The venue's point, as the `venues.location` JSON carries it, or null. */
function readVenuePoint(
  location: Json | null
): Pick<AudienceUpcomingShow, "latitude" | "longitude"> | null {
  if (!location || typeof location !== "object" || Array.isArray(location)) {
    return null;
  }
  const { latitude, longitude } = location;
  return typeof latitude === "number" && typeof longitude === "number"
    ? { latitude, longitude }
    : null;
}

/**
 * Reads a comedian's upcoming shows with their venues' points, soonest first.
 * The same read the legacy dashboard map makes, cut to shows from today on. A
 * show whose venue has no name or no point is left out: the list cannot place
 * it in a market.
 */
export async function getAudienceUpcomingShows(
  supabase: SupabaseClient<Database>,
  comedianId: string
): Promise<AudienceUpcomingShow[]> {
  const { data, error } = await supabase
    .from("comedian_shows")
    .select("datetime, venues (id, name, location)")
    .eq("comedian_id", comedianId)
    .gte("datetime", dayjs.utc().format("YYYY-MM-DD"))
    .order("datetime", { ascending: true });

  if (error) {
    logException(error, {
      extra: { comedianId, context: "getAudienceUpcomingShows" },
    });
    throw new Error(
      `Error fetching upcoming shows for comedian ${comedianId}: ${error.message}`
    );
  }

  return (data ?? []).flatMap((row) => {
    const point = readVenuePoint(row.venues?.location ?? null);
    return row.datetime && row.venues?.name && point
      ? [
          {
            datetime: row.datetime,
            venueId: row.venues.id,
            venue: row.venues.name,
            ...point,
          },
        ]
      : [];
  });
}

/** Where a contact row came from: the comedian's Punchup page, or a CSV import. */
export type AllContactsSource = "punchup" | "imported";

/** One row of the All Contacts table. */
export interface AllContactsRow {
  /** The subscriber's user id or the imported contact's id; the table's row key. */
  id: string;
  email: string | null;
  phone: string | null;
  /** When the person subscribed, as an ISO timestamp. Nullable in the schema. */
  addedAt: string | null;
  city: string | null;
  state: string | null;
  stateAbbreviation: string | null;
  source: AllContactsSource;
}

export interface AllContactsPage {
  rows: AllContactsRow[];
  /** True when another page follows this one. */
  hasMore: boolean;
}

/** A city as the audience names it: the city and the state abbreviation. */
export interface AllContactsCity {
  city: string;
  state: string;
}

/** The city a read is centred on, and how far from it a contact may live. */
export interface AllContactsNearCity extends AllContactsCity {
  radiusMiles: number;
}

export interface AllContactsPageOptions {
  limit: number;
}

export interface ContactsNearCityOptions extends AllContactsPageOptions {
  /** The city, and the radius in miles of the box read around it. */
  near: AllContactsNearCity;
}

export interface ContactsNearCityPage extends AllContactsPage {
  /** How many contacts live in the box across every page. */
  cityTotal: number;
}

/**
 * The columns every audience read returns, typed from the view, where every
 * column is nullable: Postgres carries no NOT NULL through a view.
 * `toAllContactsRow` is the one place that narrows them.
 */
type AudienceContactRow = Pick<
  Database["public"]["Views"]["audience_contacts"]["Row"],
  | "id"
  | "source"
  | "created_at"
  | "email"
  | "phone"
  | "city"
  | "state"
  | "state_abbreviation"
>;

const AUDIENCE_CONTACT_COLUMNS =
  "id, source, created_at, email, phone, city, state, state_abbreviation";

const ALL_CONTACTS_SOURCES: Record<AllContactsSource, true> = {
  punchup: true,
  imported: true,
};

function isAllContactsSource(value: string | null): value is AllContactsSource {
  return value !== null && value in ALL_CONTACTS_SOURCES;
}

/**
 * Narrows an audience row to a table row. Every branch of the view selects a
 * primary key and a literal source, so a null here is a broken read, not
 * missing data.
 */
function toAllContactsRow(row: AudienceContactRow): AllContactsRow {
  if (row.id === null || !isAllContactsSource(row.source)) {
    throw new Error(
      `audience_contacts row without an id or a known source: ${row.id} / ${row.source}`
    );
  }
  return {
    id: row.id,
    email: row.email,
    phone: row.phone,
    addedAt: row.created_at,
    city: row.city,
    state: row.state,
    stateAbbreviation: row.state_abbreviation,
    source: row.source,
  };
}

/** A latitude and longitude range, in degrees. */
export interface GeoBox {
  minLatitude: number;
  maxLatitude: number;
  minLongitude: number;
  maxLongitude: number;
}

const METRES_PER_MILE = 1609.344;
const METRES_PER_DEGREE = 111320;

/** The square box around `centre`: approximate, its corners reach about 1.4x `radiusMiles`. */
export function boxAroundPoint(centre: GeoPoint, radiusMiles: number): GeoBox {
  const metres = radiusMiles * METRES_PER_MILE;
  const halfHeight = metres / METRES_PER_DEGREE;
  const halfWidth =
    metres / (METRES_PER_DEGREE * Math.cos((centre.latitude * Math.PI) / 180));
  return {
    minLatitude: centre.latitude - halfHeight,
    maxLatitude: centre.latitude + halfHeight,
    minLongitude: centre.longitude - halfWidth,
    maxLongitude: centre.longitude + halfWidth,
  };
}

type PostalCodePoint = Pick<
  Database["public"]["Tables"]["postal_codes"]["Row"],
  "latitude" | "longitude" | "country_code"
>;

/** The average point of the postal codes in the country with most of them (tie: lowest code). */
function cityCentre(postalCodes: PostalCodePoint[]): GeoPoint | null {
  const pointsByCountry = new Map<string, GeoPoint[]>();
  for (const { latitude, longitude, country_code } of postalCodes) {
    if (latitude === null || longitude === null || country_code === null) {
      continue;
    }
    const points = pointsByCountry.get(country_code) ?? [];
    points.push({ latitude, longitude });
    pointsByCountry.set(country_code, points);
  }

  const [largest] = [...pointsByCountry.entries()].sort(
    ([codeA, pointsA], [codeB, pointsB]) =>
      pointsB.length - pointsA.length || codeA.localeCompare(codeB)
  );
  return largest ? averagePoint(largest[1]) : null;
}

/** The box around the city's centre, or null for a city `postal_codes` does not know; a stand-in until the city pipeline tables land. */
async function readBoxAroundCity(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  near: AllContactsNearCity
): Promise<GeoBox | null> {
  const { data, error } = await supabase
    .from("postal_codes")
    .select("latitude, longitude, country_code")
    .eq("city", near.city)
    .eq("state_abbreviation", near.state);

  if (error) {
    logException(error, {
      extra: { comedianId, near, context: "readBoxAroundCity" },
    });
    throw new Error(
      `Error reading the centre of ${near.city}, ${near.state}: ${error.message}`
    );
  }

  const centre = cityCentre(data ?? []);
  return centre && boxAroundPoint(centre, near.radiusMiles);
}

/** An `audience_contacts` read that the box filters can narrow. */
interface BoxFilterable<Query> {
  gte(column: "latitude" | "longitude", value: number): Query;
  lte(column: "latitude" | "longitude", value: number): Query;
}

/** Narrows an `audience_contacts` read to the contacts inside `box`. */
function withinBox<Query extends BoxFilterable<Query>>(
  query: Query,
  box: GeoBox
): Query {
  return query
    .gte("latitude", box.minLatitude)
    .lte("latitude", box.maxLatitude)
    .gte("longitude", box.minLongitude)
    .lte("longitude", box.maxLongitude);
}

/** The audience, or the part inside `box`, newest first, one row past `limit`. */
function readAudiencePage(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  limit: number,
  box: GeoBox | null
) {
  const audience = supabase
    .from("audience_contacts")
    .select(AUDIENCE_CONTACT_COLUMNS)
    .eq("comedian_id", comedianId);

  return (box ? withinBox(audience, box) : audience)
    .order("created_at", { ascending: false })
    .limit(limit + 1);
}

/** Throws on a failed page read; otherwise turns the one row past `limit` into `hasMore`. */
function toAllContactsPage(
  {
    data,
    error,
  }: { data: AudienceContactRow[] | null; error: PostgrestError | null },
  limit: number,
  extra: { comedianId: string; context: string; near?: AllContactsNearCity }
): AllContactsPage {
  if (error) {
    logException(error, { extra: { ...extra, limit } });
    throw new Error(
      `Error fetching contacts for comedian ${extra.comedianId}: ${error.message}`
    );
  }

  const contacts = data ?? [];
  const hasMore = contacts.length > limit;
  const shownRows = hasMore ? contacts.slice(0, limit) : contacts;

  return { rows: shownRows.map(toAllContactsRow), hasMore };
}

/**
 * Reads a comedian's contacts from the `audience_contacts` view, newest
 * first, up to `limit` rows. The view holds the followers snapshot with the
 * imported contacts, each row naming its source. A subscriber appears once
 * `update_comedian_followers_details()` has run, and only if they have a
 * zipcode and an email or a phone; an imported contact appears as soon as the
 * import commits.
 */
export async function getAllContactsPage(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  { limit }: AllContactsPageOptions
): Promise<AllContactsPage> {
  // One row past the run, so `hasMore` costs no extra count query.
  const result = await readAudiencePage(supabase, comedianId, limit, null);
  return toAllContactsPage(result, limit, {
    comedianId,
    context: "getAllContactsPage",
  });
}

/**
 * The contacts inside the box around one city, as `getAllContactsPage` reads
 * them, with how many live in the box across every page. It reads the box
 * once, then the page and the count side by side, so the total and the rows
 * describe one set. A city `postal_codes` does not know has no contacts.
 */
export async function getContactsNearCity(
  supabase: SupabaseClient<Database>,
  comedianId: string,
  { limit, near }: ContactsNearCityOptions
): Promise<ContactsNearCityPage> {
  const box = await readBoxAroundCity(supabase, comedianId, near);
  if (!box) return { rows: [], hasMore: false, cityTotal: 0 };

  const [result, [cityTotal]] = await Promise.all([
    readAudiencePage(supabase, comedianId, limit, box),
    countRows(
      [
        withinBox(
          supabase
            .from("audience_contacts")
            .select("id", { count: "exact", head: true })
            .eq("comedian_id", comedianId),
          box
        ),
      ],
      { comedianId, context: "getContactsNearCity" }
    ),
  ]);

  return {
    ...toAllContactsPage(result, limit, {
      comedianId,
      near,
      context: "getContactsNearCity",
    }),
    cityTotal,
  };
}
